/**
 * React hook for Microsoft Entra ID authentication using MSAL.
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  useMsal, 
  useAccount, 
  useIsAuthenticated 
} from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest, apiRequest } from '../config/authConfig';
import { registerTokenProvider } from '../services/apiWithAuth';
import useAppStore from '../store/useAppStore';

export const useAuth = () => {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const isAuthenticated = useIsAuthenticated();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  
  const { currentEmployee, setCurrentEmployee, employees } = useAppStore();

  // Funkcja logowania
  const login = useCallback(async () => {
    if (inProgress !== 'none') {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      setError(error.message || 'Login failed');
      setIsLoading(false);
    }
  }, [instance, inProgress]);

  // Funkcja wylogowania
  const logout = useCallback(async () => {
    if (inProgress !== 'none') {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin
      });
      setAccessToken(null);
    } catch (error) {
      console.error('Logout failed:', error);
      setError(error.message || 'Logout failed');
      setIsLoading(false);
    }
  }, [instance, inProgress]);

  // Funkcja pobierania tokenu dostępu
  const getAccessToken = useCallback(async (forceRefresh = false) => {
    if (!account) {
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account: account,
        forceRefresh
      });
      
      const token = response.accessToken;
      setAccessToken(token);
      return token;
    } catch (error) {
      console.warn('Silent token acquisition failed:', error);
      
      // NIE RÓB acquireTokenRedirect tutaj - to powoduje pętlę!
      // Token zostanie zdobyty przy następnym loginRedirect
      setError('Token expired - please login again');
      return null;
    }
  }, [instance, account]);

  // Automatyczne pobieranie tokenu po zalogowaniu
  useEffect(() => {
    if (isAuthenticated && account && !accessToken) {
      getAccessToken();
    }
  }, [isAuthenticated, account, accessToken, getAccessToken]);

  // Rejestracja funkcji pobierania tokenu dla API
  useEffect(() => {
    if (isAuthenticated) {
      registerTokenProvider(getAccessToken);
    }
  }, [isAuthenticated, getAccessToken]);

  // Automatyczne ładowanie danych zalogowanego pracownika
  useEffect(() => {
    if (isAuthenticated && account && employees.length > 0 && !currentEmployee) {
      const userEmail = account.username; // W MSAL username to zazwyczaj email
      const employee = employees.find(
        emp => emp.email && emp.email.toLowerCase() === userEmail.toLowerCase()
      );
      if (employee) {
        setCurrentEmployee(employee);
        console.log('✅ Loaded current employee:', employee.code, employee.name);
      }
    }
  }, [isAuthenticated, account, employees, currentEmployee, setCurrentEmployee]);

  // Funkcja do wykonywania wywołań API z automatycznym dołączaniem tokenu
  const callApi = useCallback(async (url, options = {}) => {
    const token = await getAccessToken();
    
    if (!token) {
      throw new Error('No access token available');
    }

    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers
    };

    const response = await fetch(url, {
      ...options,
      headers
    });

    if (response.status === 401) {
      // Token wygasł, spróbuj odświeżyć
      const newToken = await getAccessToken(true);
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        return fetch(url, {
          ...options,
          headers
        });
      }
    }

    return response;
  }, [getAccessToken]);

  // Pobieranie informacji o użytkowniku
  const getUserInfo = useCallback(() => {
    if (!account) {
      return null;
    }

    return {
      id: account.localAccountId,
      username: account.username,
      name: account.name,
      email: account.username, // W MSAL username to zazwyczaj email
      claims: account.idTokenClaims || {}
    };
  }, [account]);

  // Sprawdzanie ról użytkownika
  const hasRole = useCallback((role) => {
    if (!account || !account.idTokenClaims) {
      // W trybie development bez Azure AD - wszyscy są adminami
      return role === 'Administrator' || role === 'admin';
    }

    const roles = account.idTokenClaims.roles || [];
    // Sprawdź zarówno "Admin" jak i "Administrator"
    if (role === 'Administrator' || role === 'admin') {
      return roles.includes('Admin') || roles.includes('Administrator');
    }
    return roles.includes(role);
  }, [account]);

  // Określanie roli użytkownika na podstawie Azure AD + danych pracownika
  const getUserRole = useCallback(() => {
    // W trybie development bez Azure AD
    if (!account || !account.idTokenClaims) {
      return 'Administrator';
    }

    const roles = account.idTokenClaims.roles || [];
    
    // Sprawdź czy ma rolę Admin/Administrator w Azure AD
    if (roles.includes('Admin') || roles.includes('Administrator')) {
      return 'Administrator';
    }
    
    // Sprawdź czy jest serwisantem (na podstawie is_service w danych pracownika)
    if (currentEmployee && currentEmployee.is_service) {
      return 'Serwisant';
    }
    
    // Dla pozostałych - domyślnie Pracownik
    return 'Pracownik';
  }, [account, currentEmployee]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Stan
    isAuthenticated,
    isLoading: isLoading || inProgress !== 'none',
    error,
    user: getUserInfo(),
    accessToken,
    
    // Funkcje
    login,
    logout,
    getAccessToken,
    callApi,
    clearError,
    hasRole,
    getUserRole,
    
    // Pomocnicze
    account,
    instance
  };
};